<template>
  <s-modal
    v-model="active"
    size="lg"
    cancel-confirm-message-code="MC006"
    ok-confirm-message-code="MC011"
    hide-header-close
    :is-loading="isLoading || loadingContact"
    :title="editContactTitle"
    :ok-disabled="$v.$invalid"
    :confirm-type="confirmationTypes.YES_NO"
    :ok-title="$t('general.confirm')"
    @cancel="closeModal"
    @ok="submit"
    @show="onShow"
  >
    <b-container fluid>
      <b-row
        align-h="between"
        align-v="center"
      >
        <b-col
          cols="12"
          sm="9"
        >
          <b-form-group>
            <label>
              {{ $t('sensors.configuration.description') }}*
            </label>
            <b-form-input
              v-model="$v.contact.description.$model"
              :maxlength="maxAlarmDescriptionLength"
              :state="$bsStateValidator($v.contact.description.$error)"
              @blur="updateContactName"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-checkbox
            class="text-center"
            v-model="contact.registerStatus"
            :value="registerStatus.ACTIVE"
            :unchecked-value="registerStatus.INACTIVE"
            @change="activeContactHandler"
          >
            {{ $t('sensors.configuration.active-contact') }}
          </b-checkbox>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          sm="9"
        >
          <b-form-group>
            <label>
              {{ $t('sensors.configuration.start-position') }}*
            </label>
            <s-select
              v-model="contact.alarmInitialStatus"
              value-field="id"
              text-field="name"
              :options="alarmStatusTypes"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="auto">
          <b-container
            fluid
            class="px-0 pt-1"
          >
            <b-row
              align-v="center"
              no-gutters
            >
              <b-col cols="auto">
                <b-form-checkbox
                  v-model="contact.activeAlarm"
                  :disabled="disableAlarm"
                >
                  {{ $t('sensors.configuration.activate-alarm') }}
                </b-form-checkbox>
              </b-col>
              <b-col
                cols="auto"
                class="pl-2"
              >
                <info-badge>
                  <span v-html="activeAlarmTooltipContent" />
                </info-badge>
              </b-col>
            </b-row>
          </b-container>
        </b-col>
      </b-row>
      <b-row>
        <alarm-details
          class="alarm-details"
          :visible-alarm="contact.activeAlarm"
          :details="alarmDetails"
        />
      </b-row>
    </b-container>
  </s-modal>
</template>

<script>
import { InitialState, InstallationStatus } from 'sigma-mappings';
import confirmationTypes from '@/constants/confirmationTypes';
import InfoBadge from '@/components/icon/InfoBadge';
import AlarmDetails from '@/domain/components/installation/supervision/sensors/AlarmDetails';
import ContactDM1Service from '@/domain/services/installation/supervision/sensors/contact';
import {
  required,
  maxLength,
} from 'vuelidate/lib/validators';

export default {
  components: {
    InfoBadge,
    AlarmDetails,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    editingContact: {
      type: String,
    },
    sensorId: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    usedNames: {
      type: Array,
      default: () => ([]),
    },
  },
  data: () => ({
    contact: {},
    contactTypes: [],
    alarmStatusTypes: [],
    showConfirmModal: false,
    maxAlarmDescriptionLength: 100,
    loadingContact: false,
    confirmationTypes,
  }),
  validations() {
    const haveValidName = () => this.haveValidName;

    return {
      contact: {
        description: {
          required,
          haveValidName,
          maxLength: maxLength(100),
        },
        alarmInitialStatus: {
          required,

        },
      },
    };
  },
  computed: {
    haveValidName() {
      return !this.usedNames
        .includes((this.contact?.description || '')
          .trim()
          .toLowerCase());
    },
    alarmDetails() {
      return this.contact.alarmDetails;
    },
    disableAlarm() {
      return this.contact.registerStatus !== InstallationStatus.ACTIVE;
    },
    registerStatus() {
      return {
        ACTIVE: InstallationStatus.ACTIVE,
        INACTIVE: InstallationStatus.INACTIVE,
      };
    },
    activeAlarmTooltipContent() {
      return this.$t('sensors.configuration.contact-hint', {
        alarmsAndWarningsScreenRef: this.alarmsAndWarningsHighlightContent,
      });
    },
    alarmsAndWarningsHighlightContent() {
      return `<a class="dm1-edit-modal__info-highlight-text" href="${this.alarmsAndWarningsUrl
        }" target="_blank">${this.$t('page-name.alarms-and-warnings')
        }</a>`;
    },
    alarmsAndWarningsUrl() {
      const routeData = this.$router.resolve({
        name: 'alarms-and-warnings',
      });
      return routeData.href;
    },
    editContactTitle() {
      return this.$t('sensors.configuration.update-contact', {
        name: this.contact.name,
      });
    },
  },
  methods: {
    updateContactName() {
      this.alarmDetails.contactDescription = this.contact.description;
    },
    closeModal() {
      this.$emit('close');
    },
    submit() {
      const payload = { ...this.contact };
      this.$emit('submit', payload);
    },
    onShow() {
      this.loadAlarmStatusTypes();
      this.contact = {};
      this.contact.id = this.editingContact;
      this.contact.sensorId = this.sensorId;
      this.loadAlarmDetails();
    },
    loadAlarmStatusTypes() {
      this.alarmStatusTypes = Object.values(InitialState)
        .map(initialState => ({
          name: this.$t(`enums.initial-state.${initialState}`),
          id: initialState,
        }));
    },
    loadAlarmDetails() {
      this.loadingContact = true;
      ContactDM1Service.getContactById(this.contact.sensorId, this.contact.id)
        .then((data) => {
          const { registerStatus, alarmDetails, ...payload } = data;
          this.contact = {
            ...this.contact,
            ...payload,
            registerStatus: registerStatus ?? InstallationStatus.INACTIVE,
            alarmDetails: {
              ...alarmDetails,
              contactName: payload.name,
              contactDescription: payload?.description ?? '',
            },
          };
        })
        .finally(() => { this.loadingContact = false; });
    },
    activeContactHandler() {
      this.contact.activeAlarm = false;
    },
  },
};
</script>

<style lang="sass" scoped>
::v-deep .dm1-edit-modal
  &__info-highlight-text
    text-decoration: underline
    color: var(--color-info)

    &:hover
      text-decoration: underline
      color: var(--color-info)
      opacity: .8

::v-deep span.select__dropdown-content
  color: var(--font-color-primary)
</style>
